import { DateTime } from 'luxon';
import { BFMethod, Gender } from '../enums';
import { roundToPrecision } from '../shared-util';
import { BFData } from './user-state';

/** The names of the five skinfold sites, for the "please measure this one" message. */
export type SkinfoldSite = 'chest' | 'tricep' | 'umbilical' | 'thigh' | 'suprailiac';

/**
 * Every message `calculateBF` can produce, supplied by the caller.
 *
 * Shared code cannot import the client's `LL`, so the words come in as a parameter. The generic
 * `T` carries the caller's string type out again: the client passes LL-backed functions (which
 * return the branded `TranslatedString`), so the returned message is branded too and needs no
 * cast. A unit test can pass plain strings and gets plain `string` back (#4368).
 */
export interface BfErrorTexts<T extends string = string> {
  /** "Please enter at least one value for the '<skinfold>' skinfold." */
  missingSkinfold: (skinfold: T) => T;
  skinfoldNames: { [site in SkinfoldSite]: () => T };
  genderRequired: () => T;
  tapeAllValues: () => T;
  tapeInvalid: () => T;
  noMethod: () => T;
  /** the computed percentage is outside any plausible human range */
  valuesOutOfRange: () => T;
}

export function calculateBF<T extends string>(
  bfData: BFData,
  gender: Gender,
  born: string,
  height_cm: number,
  texts: BfErrorTexts<T>,
): T | null {
  let age = -DateTime.fromISO(born).diffNow().as('years');
  let height = height_cm;

  let message: T | null = null;
  switch (bfData.method) {
    case BFMethod.Self:
    if (!bfData.bfSelf_pc) {
        break;
      }

    bfData.bf_pc = bfData.bfSelf_pc;
      break;
    case BFMethod.Caliper:
      // calculate averages first
      let avg: {
        chest?: number;
        suprailiac?: number;
        thigh?: number;
        tricep?: number;
        umbilical?: number;
      } = {};

      let average = (a: number[]) => {
        let sum = a.reduce((acc, v) => acc + (v ? v : 0), 0);
        let num = a.reduce((acc, v) => acc + (v ? 1 : 0), 0);
        if (isFinite(sum) && isFinite(num) && num > 0) {
          return sum / num;
        } else {
          return null;
        }
      };

      avg.chest = average(bfData.chest_mm);
      avg.suprailiac = average(bfData.suprailiac_mm);
      avg.thigh = average(bfData.thigh_mm);
      avg.tricep = average(bfData.tricep_mm);
      avg.umbilical = average(bfData.umbilical_mm);

      let race = 0; // for now, as per spec

      switch (gender) {
        case Gender.male: {
          let missing: SkinfoldSite | null = null;
          if (!avg.chest) {
            missing = 'chest';
          } else if (!avg.tricep) {
            missing = 'tricep';
          } else if (!avg.umbilical) {
            missing = 'umbilical';
          } else if (!avg.thigh) {
            missing = 'thigh';
          }

          if (missing) {
            message = texts.missingSkinfold(texts.skinfoldNames[missing]());
            break;
          }

          let cut = avg.chest + avg.umbilical + avg.thigh;

          let jpw3 = 457 / (1.10938 - 0.0008267 * cut + 0.0000016 * cut * cut - 0.0002575 * age) - 414.2;
          let F3 = 8.997 + 0.24658 * (avg.umbilical + avg.thigh + avg.tricep) - 6.343 - 1.998 * race;
          let mod3 = 0.132 * (avg.chest + avg.tricep) + 3.53;

          bfData.bf_pc = roundToPrecision((jpw3 + F3 + mod3) / 3, 0.1);
          break;
        }
        case Gender.female: {
          let missing: SkinfoldSite | null = null;
          if (!avg.tricep) {
            missing = 'tricep';
          } else if (!avg.suprailiac) {
            missing = 'suprailiac';
          } else if (!avg.umbilical) {
            missing = 'umbilical';
          } else if (!avg.thigh) {
            missing = 'thigh';
          }

          if (missing) {
            message = texts.missingSkinfold(texts.skinfoldNames[missing]());
            break;
          }

          let stt = avg.suprailiac + avg.thigh + avg.tricep;
          let c3 = 7.787 + 0.24658 * (avg.umbilical + avg.thigh + avg.tricep);
          let jpw3 = 457 / (1.0994921 - 0.0009929 * stt + 0.0000023 * stt * stt - 0.0001392 * age) - 414.2;
          let F3 = 8.997 + 0.24658 * (avg.umbilical + avg.thigh + avg.tricep) - 1.998 * race;

          bfData.bf_pc = roundToPrecision((c3 + jpw3 + F3) / 3, 0.1);
          break;
        }
        default:
          bfData.bf_pc = undefined;
          message = texts.genderRequired();
          break;
      }
      break;
    case BFMethod.Tape:
      switch (gender) {
        case Gender.male:
          if (!bfData.abdomen_cm || !bfData.neck_cm) {
            message = texts.tapeAllValues();
            break;
          }

          if (bfData.abdomen_cm < 10 || bfData.abdomen_cm > 200 || bfData.neck_cm < 10 || bfData.neck_cm > 100) {
            message = texts.tapeInvalid();
            break;
          }

          bfData.bf_pc = roundToPrecision(495 / (1.0324 - 0.19077 * Math.log10(bfData.abdomen_cm - bfData.neck_cm) + 0.15456 * Math.log10(height)) - 450, 0.1);
          break;
        case Gender.female:
          if (!bfData.waist_cm || !bfData.hip_cm || !bfData.neck_cm) {
            message = texts.tapeAllValues();
            break;
          }

          if (
            bfData.waist_cm < 10 ||
            bfData.waist_cm > 200 ||
            bfData.hip_cm < 10 ||
            bfData.hip_cm > 200 ||
            bfData.neck_cm < 10 ||
            bfData.neck_cm > 100
          ) {
            message = texts.tapeInvalid();
            break;
          }

          bfData.bf_pc = roundToPrecision(495 / (1.29579 - 0.35004 * Math.log10(bfData.waist_cm + bfData.hip_cm - bfData.neck_cm) + 0.221 * Math.log10(height)) - 450, 0.1);

          break;
        default:
          bfData.bf_pc = undefined;
          message = texts.genderRequired();
          break;
      }
      break;
    default:
      message = texts.noMethod();
      break;
  }
  if (
    bfData.bf_pc > 60 ||
    (gender == Gender.male && bfData.bf_pc < 2) ||
    (gender == Gender.female && bfData.bf_pc < 5)
  ) {
    return texts.valuesOutOfRange();
  }

  return message;
}
